import {
  useLocationHash,
  useLocationState,
  useNavigate,
} from '@rocketlane/rocket-router';
import { useCallback, useMemo } from 'react';

const PPI_IN_ROUTE_REGEX = /#ppi=(\d+)/;

export const useCustomerPortalHashMap = () => {
  const hash = useLocationHash();
  const state = useLocationState();
  const navigate = useNavigate();
  const hashMap = useMemo(() => {
    const split = hash.replace('#', '').split('&');

    const parsed = split.reduce((acc, curr) => {
      const [key, value] = curr.split('=');

      if (!key) {
        return acc;
      }

      let decodedValue: string;

      try {
        decodedValue = decodeURIComponent(value || '');
      } catch {
        decodedValue = value || '';
      }
      acc[key] = decodedValue;

      // Extract ppi from route value if it contains #ppi=X
      if (key === 'route' && decodedValue.includes('#ppi=')) {
        const ppiMatch = decodedValue.match(PPI_IN_ROUTE_REGEX);

        if (ppiMatch) {
          const [, ppiValue] = ppiMatch;

          if (ppiValue) {
            acc.ppi = ppiValue;
            // Clean the route value to remove the ppi part
            acc[key] = decodedValue
              .replace(PPI_IN_ROUTE_REGEX, '')
              .replace(/#$/, '');
          }
        }
      }

      return acc;
    }, {} as Record<string, string>);

    return parsed;
  }, [hash]);

  const constructHash = useCallback(
    (entries?: Record<string, unknown>) => {
      return Object.entries(entries ?? hashMap)
        .filter(([key]) => !!key)

        .map(([key, value]) => `${key}=${value}`)
        .join('&');
    },
    [hashMap],
  );

  const setHashMap = useCallback(
    (_key: string, _value: string) => {
      hashMap[_key] = _value;

      const newHash = constructHash();
      const currentSearch = window.location.search;

      navigate(
        {
          hash: newHash,
          search: currentSearch,
        },
        {
          replace: true,
        },
      );
    },
    [constructHash, hashMap, navigate],
  );

  const pushToHashMap = useCallback(
    (_key: string, _value: string) => {
      hashMap[_key] = _value;

      const newHash = constructHash();

      navigate(
        {
          hash: newHash,
          search: window.location.search,
        },
        { replace: true },
      );
    },
    [constructHash, hashMap, navigate],
  );

  const batchUpdate = useCallback(
    (
      updates: Record<string, string>,
      navOptions?: {
        state?: Record<string, unknown>;
        replace?: boolean;
      },
    ) => {
      Object.entries(updates).forEach(([key, value]) => {
        hashMap[key] = value;
      });

      const newHash = constructHash();

      navigate(
        { hash: newHash, search: window.location.search },
        { replace: navOptions?.replace ?? true, state: navOptions?.state },
      );
    },
    [constructHash, hashMap, navigate],
  );

  const removeKey = useCallback(
    (_key: string) => {
      delete hashMap[_key];

      const newHash = constructHash();

      navigate(
        {
          hash: newHash,
          search: window.location.search,
        },
        { replace: true },
      );
    },
    [constructHash, hashMap, navigate],
  );

  const removeKeys = useCallback(
    (_keys: string[]) => {
      _keys.forEach((key) => {
        delete hashMap[key];
      });

      const newHash = constructHash();

      navigate(
        {
          hash: newHash,
          search: window.location.search,
        },
        {
          state,
        },
      );
    },
    [constructHash, hashMap, navigate, state],
  );

  const overwriteHashMap = useCallback(
    (newHashMap: Record<string, string>) => {
      const newHash = constructHash(newHashMap);

      navigate(
        {
          hash: newHash,
          search: window.location.search,
        },
        { replace: true, state },
      );
    },
    [constructHash, navigate, state],
  );

  return {
    hashMap,
    setHashMap,
    removeKey,
    pushToHashMap,
    batchUpdate,
    overwriteHashMap,
    removeKeys,
  };
};
